// Display formatting for KPI values. `unit` comes from the metric catalog.
import type { MetricUnit } from "@/lib/api/types";

/** Compact number: 1.2K, 3.4M, 1.1B. */
export function compact(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${trim(n / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (abs >= 1_000) return `${trim(n / 1_000)}K`;
  return Math.round(n).toLocaleString("en-US");
}

function trim(n: number): string {
  return (Math.round(n * 10) / 10).toLocaleString("en-US", { maximumFractionDigits: 1 });
}

/** Full integer with thousands separators. */
export function int(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Math.round(n).toLocaleString("en-US");
}

export function currency(n: number | null | undefined, opts?: { compact?: boolean }): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  if (opts?.compact) return `€${compact(n)}`;
  return `€${Math.round(n).toLocaleString("en-US")}`;
}

export function percent(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

export function ratio(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}×`;
}

/** Format a value according to its catalog unit. Currency/number use compact form.
 *  Pass `money` to render currency values in a specific display currency. */
export function formatMetric(
  value: number | null | undefined,
  unit: MetricUnit,
  money?: (v: number | null | undefined) => string,
): string {
  switch (unit) {
    case "currency": return money ? money(value) : currency(value, { compact: true });
    case "percent":  return percent(value);
    case "ratio":    return ratio(value);
    case "number":
    default:         return compact(value);
  }
}

/** Signed percentage delta vs a baseline, e.g. "+12.4%". null when baseline is 0. */
export function deltaPct(current: number, baseline: number): number | null {
  if (!baseline) return null;
  return (current - baseline) / baseline;
}

export function signedPct(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const v = n * 100;
  return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%`;
}

/** Short relative time ("vandaag", "3 d geleden", or a date for older). */
export function relativeDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return "vandaag";
  if (days === 1) return "gisteren";
  if (days < 30) return `${days} d geleden`;
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}
